import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { SignedAvatarImage } from '@/components/shared/SignedAvatarImage';
import { useProfiles } from '@/hooks/useProfile';
import { Users } from 'lucide-react';

interface MentionInputProps {
  value: string;
  onChange: (value: string) => void;
  onFocus?: () => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  placeholder?: string;
  className?: string;
}

interface MentionOption {
  id: string;
  display: string;
  avatar?: string | null;
  isEveryone?: boolean;
}

export function MentionInput({ value, onChange, onFocus, onKeyDown, placeholder, className }: MentionInputProps) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionStart, setMentionStart] = useState<number | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { data: profiles } = useProfiles();

  // Build mention options
  const mentionOptions: MentionOption[] = [
    { id: 'everyone', display: 'everyone', isEveryone: true },
    ...(profiles || []).map(p => ({
      id: p.user_id,
      display: p.full_name || 'Unknown',
      avatar: p.avatar_url,
    })),
  ];

  // Filter options based on search
  const filteredOptions = mentionOptions.filter(opt =>
    opt.display.toLowerCase().includes(mentionSearch.toLowerCase())
  ).slice(0, 8);

  // Handle text changes
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    const cursorPos = e.target.selectionStart;
    onChange(newValue);

    // Check if we're in a mention context
    const textBeforeCursor = newValue.slice(0, cursorPos);
    const atMatch = textBeforeCursor.match(/@(\w*)$/);

    if (atMatch) {
      setMentionStart(cursorPos - atMatch[0].length);
      setMentionSearch(atMatch[1]);
      setShowDropdown(true);
      setSelectedIndex(0);
    } else {
      setShowDropdown(false);
      setMentionStart(null);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (showDropdown && filteredOptions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(i => Math.min(i + 1, filteredOptions.length - 1));
        return;
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(i => Math.max(i - 1, 0));
        return;
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertMention(filteredOptions[selectedIndex]);
        return;
      } else if (e.key === 'Escape') {
        setShowDropdown(false);
        return;
      }
    }
    // Pass through to parent handler if not handled
    onKeyDown?.(e);
  };

  // Insert a mention
  const insertMention = useCallback((option: MentionOption) => {
    if (mentionStart === null) return;

    const before = value.slice(0, mentionStart);
    const after = value.slice(textareaRef.current?.selectionStart || mentionStart);

    // Format: @[id:display] for users, @everyone for everyone
    const mentionText = option.isEveryone
      ? '@everyone'
      : `@[${option.id}:${option.display}]`;

    const newValue = before + mentionText + ' ' + after.replace(/^\w*/, '');
    onChange(newValue);
    setShowDropdown(false);
    setMentionStart(null);

    // Focus back and set cursor position
    setTimeout(() => {
      if (textareaRef.current) {
        const newPos = before.length + mentionText.length + 1;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newPos, newPos);
      }
    }, 0);
  }, [mentionStart, value, onChange]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        textareaRef.current &&
        !textareaRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative">
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={onFocus}
        placeholder={placeholder}
        className={className}
      />

      {showDropdown && filteredOptions.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 mt-1 w-64 max-h-64 overflow-y-auto bg-popover border border-border rounded-lg shadow-lg"
        >
          {filteredOptions.map((option, index) => (
            <button
              key={option.id}
              type="button"
              onClick={() => insertMention(option)}
              className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted transition-colors ${
                index === selectedIndex ? 'bg-muted' : ''
              }`}
            >
              {option.isEveryone ? (
                <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <Users className="h-4 w-4 text-primary" />
                </div>
              ) : (
                <Avatar className="h-8 w-8">
                  <SignedAvatarImage src={option.avatar || undefined} />
                  <AvatarFallback className="bg-primary/10 text-primary text-xs">
                    {option.display.charAt(0)}
                  </AvatarFallback>
                </Avatar>
              )}
              <span className="font-medium">
                {option.isEveryone ? '@everyone' : option.display}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
